// Llaves: lo único que recibe un agente o una aplicación. Nunca la clave de un proveedor ni la de un sistema
// conectado. Cada llave dice qué modelos y qué herramientas puede usar, cuánto puede gastar al mes y a qué ritmo.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cargando, Input, Modal, PageHead, Tabla, Tag, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde, numero, usd } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import { AvisoSoloVer, Casilla, Copiable, Estado, SoloSiAdministra } from '../componentes/Comunes';
import { ComoConectar } from '../componentes/ComoConectar';

interface Form {
  id: string | null; nombre: string; modelos: string[]; herramientas: string[]; presupuestoMensual: string; limiteRpm: string;
  ocultarDatosPersonales: boolean; diasVigencia: string;
}
const vacia = (): Form => ({ id: null, nombre: '', modelos: [], herramientas: [], presupuestoMensual: '', limiteRpm: '', ocultarDatosPersonales: false, diasVigencia: '' });
const alternar = (lista: string[], v: string, si: boolean) => (si ? [...new Set([...lista, v])] : lista.filter((x) => x !== v));

export function Llaves() {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const llaves = useQuery({ queryKey: ['llaves'], queryFn: () => api<any[]>('/api/llaves') });
  const modelos = useQuery({ queryKey: ['modelos'], queryFn: () => api<any[]>('/api/modelos') });
  const herramientas = useQuery({ queryKey: ['herramientas'], queryFn: () => api<any[]>('/api/herramientas') });
  const [form, setForm] = useState<Form | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nueva, setNueva] = useState<{ llave: string; modelo: string } | null>(null);

  // Las herramientas agrupadas por su conector, para conceder un conector entero o una por una.
  const porConector = useMemo(() => {
    const g = new Map<string, { nombre: string; titulo: string }[]>();
    for (const t of herramientas.data ?? []) {
      const [c] = (t.name as string).split('__');
      g.set(c!, [...(g.get(c!) ?? []), { nombre: t.name, titulo: t.description }]);
    }
    return [...g.entries()];
  }, [herramientas.data]);

  const guardar = useMutation({
    mutationFn: (f: Form) => api<{ id: string; llave?: string }>(f.id ? `/api/llaves/${f.id}` : '/api/llaves', {
      nombre: f.nombre, modelos: f.modelos, herramientas: f.herramientas,
      presupuestoMensual: f.presupuestoMensual === '' ? null : Number(f.presupuestoMensual),
      limiteRpm: f.limiteRpm === '' ? null : Number(f.limiteRpm),
      ocultarDatosPersonales: f.ocultarDatosPersonales,
      ...(f.id ? {} : { diasVigencia: f.diasVigencia === '' ? null : Number(f.diasVigencia) }),
    }),
    onSuccess: (r, f) => {
      void qc.invalidateQueries({ queryKey: ['llaves'] });
      setForm(null);
      if (r.llave) setNueva({ llave: r.llave, modelo: f.modelos.find((m) => m !== '*') ?? modelos.data?.[0]?.nombre ?? 'rapido' });
      else aviso('Llave cambiada');
    },
    onError: (e) => setError(mensajeDeError(e)),
  });
  const estado = useMutation({
    mutationFn: ({ id, estado }: { id: string; estado: string }) => api(`/api/llaves/${id}/estado`, { estado }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['llaves'] }); aviso('Listo'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  if (llaves.isLoading) return <Cargando />;
  const permisos = (l: any) => [
    l.modelos.includes('*') ? 'Todos los modelos' : l.modelos.length ? `${l.modelos.length} modelo${l.modelos.length === 1 ? '' : 's'}` : null,
    l.herramientas.includes('*') ? 'todas las herramientas' : l.herramientas.length ? `${l.herramientas.length} permiso${l.herramientas.length === 1 ? '' : 's'} de herramientas` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="page">
      <PageHead title="Llaves" subtitle="Una llave por agente o aplicación. Sirve para los modelos y para el MCP del negocio, y se pausa o se revoca sin tocar ninguna credencial. Los agentes y las rutinas del gateway tienen la suya, interna.">
        <SoloSiAdministra><Button icon="plus" onClick={() => { setError(null); setForm(vacia()); }}>Nueva llave</Button></SoloSiAdministra>
      </PageHead>
      <AvisoSoloVer />
      {!llaves.data?.length ? (
        <Vacio titulo="Todavía no hay llaves">Crear una para el primer agente: el asistente de caja, un flujo de n8n, la app del domicilio…</Vacio>
      ) : (
        <Tabla rowKey="id" rows={llaves.data}
          columns={[
            { key: 'nombre', label: 'Llave', render: (l: any) => <div><b style={{ fontWeight: 500 }}>{l.nombre}</b><div className="muted" style={{ fontSize: 12 }}>{l.interna ? <>Interna · se cambia desde {l.nombre.startsWith('Rutina') ? 'Rutinas' : 'Agentes'}</> : <><code>gw_{l.prefijo}_…</code> · {l.creado_por?.nombre}</>}</div></div> },
            { key: 'permisos', label: 'Puede usar', render: (l: any) => <div style={{ fontSize: 13 }}>{permisos(l) || '—'}{l.ocultar_datos_personales && <div><Tag>tapa datos personales</Tag></div>}</div> },
            { key: 'gasto', label: 'Este mes', numeric: true, render: (l: any) => <span>{usd(l.gasto_mes)}{l.presupuesto_mensual !== null && <span className="muted"> / {usd(l.presupuesto_mensual)}</span>}</span> },
            { key: 'llamadas_7d', label: '7 días', numeric: true, render: (l: any) => numero(l.llamadas_7d) },
            { key: 'ultimo_uso_en', label: 'Último uso', render: (l: any) => desde(l.ultimo_uso_en) },
            { key: 'estado', label: 'Estado', render: (l: any) => (l.estado === 'activo' && l.expira_en && new Date(l.expira_en) < new Date() ? <Tag>vencida</Tag> : <Estado estado={l.estado} />) },
            { key: 'acciones', label: '', render: (l: any) => l.estado !== 'revocado' && !l.interna && (
              <SoloSiAdministra>
                <div className="fila acciones">
                  <Button size="sm" variant="secondary" icon="pencil" onClick={() => { setError(null); setForm({
                    id: l.id, nombre: l.nombre, modelos: l.modelos, herramientas: l.herramientas, presupuestoMensual: l.presupuesto_mensual === null ? '' : String(Number(l.presupuesto_mensual)),
                    limiteRpm: l.limite_rpm === null ? '' : String(l.limite_rpm), ocultarDatosPersonales: l.ocultar_datos_personales, diasVigencia: '',
                  }); }}>Cambiar</Button>
                  <Button size="sm" variant="ghost" icon={l.estado === 'activo' ? 'pause' : 'play'} onClick={() => estado.mutate({ id: l.id, estado: l.estado === 'activo' ? 'pausado' : 'activo' })}>{l.estado === 'activo' ? 'Pausar' : 'Reactivar'}</Button>
                  <Button size="sm" variant="ghost" icon="trash" onClick={() => { if (confirm(`¿Revocar «${l.nombre}»? El agente que la usa deja de entrar y no se puede deshacer.`)) estado.mutate({ id: l.id, estado: 'revocado' }); }}>Revocar</Button>
                </div>
              </SoloSiAdministra>
            ) },
          ]} />
      )}

      <div className="card" style={{ marginTop: 22, padding: '18px 20px' }}>
        <div className="h-card" style={{ marginBottom: 6 }}>Cómo se conecta un agente</div>
        <ComoConectar />
      </div>

      {form && (
        <Modal title={form.id ? `Cambiar «${form.nombre}»` : 'Nueva llave'} onClose={() => setForm(null)} width={720}
          subtitle="Lo que no se concede, no existe para quien tenga la llave: no aparece en /v1/models ni en la lista de herramientas del MCP."
          footer={<>
            <Button variant="ghost" onClick={() => setForm(null)}>Cancelar</Button>
            <Button disabled={guardar.isPending || form.nombre.trim().length < 2 || (!form.modelos.length && !form.herramientas.length)} onClick={() => { setError(null); guardar.mutate(form); }}>{form.id ? 'Guardar' : 'Crear llave'}</Button>
          </>}>
          <Input label="Para quién es" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Asistente de caja" autoFocus />

          <div className="stack" style={{ gap: 6 }}>
            <span className="etiqueta">Modelos</span>
            <div className="casillas">
              <Casilla checked={form.modelos.includes('*')} onChange={(v) => setForm({ ...form, modelos: v ? ['*'] : [] })}>Todos, también los que se creen después</Casilla>
              {!form.modelos.includes('*') && (modelos.data ?? []).map((m) => (
                <Casilla key={m.id} checked={form.modelos.includes(m.nombre)} onChange={(v) => setForm({ ...form, modelos: alternar(form.modelos, m.nombre, v) })}>
                  <code>{m.nombre}</code> <span className="muted">· {m.deGlubi ? 'de Glubi' : m.destinos.map((d: any) => d.modeloProveedor).join(' → ')}</span>
                </Casilla>
              ))}
              {!modelos.data?.length && <span className="muted" style={{ fontSize: 13 }}>No hay modelos todavía.</span>}
            </div>
          </div>

          <div className="stack" style={{ gap: 6 }}>
            <span className="etiqueta">Herramientas de los conectores</span>
            <div className="casillas" style={{ maxHeight: 280 }}>
              <Casilla checked={form.herramientas.includes('*')} onChange={(v) => setForm({ ...form, herramientas: v ? ['*'] : [] })}>Todas, también las de conectores futuros</Casilla>
              {herramientas.isLoading && <span className="muted" style={{ fontSize: 13 }}>Preguntando a los conectores…</span>}
              {!form.herramientas.includes('*') && porConector.map(([conector, lista]) => {
                const entero = form.herramientas.includes(conector);
                return (
                  <div key={conector} className="stack" style={{ gap: 4, paddingTop: 4 }}>
                    <Casilla checked={entero} onChange={(v) => setForm({ ...form, herramientas: alternar(form.herramientas.filter((h) => !h.startsWith(`${conector}__`)), conector, v) })}>
                      <b style={{ fontWeight: 500 }}>Todo «{conector}»</b> <span className="muted">({lista.length})</span>
                    </Casilla>
                    {!entero && lista.map((h) => (
                      <div key={h.nombre} style={{ paddingLeft: 22 }}>
                        <Casilla checked={form.herramientas.includes(h.nombre)} onChange={(v) => setForm({ ...form, herramientas: alternar(form.herramientas, h.nombre, v) })}>
                          <code>{h.nombre}</code>
                        </Casilla>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid-2" style={{ gap: 12 }}>
            <Input label="Tope al mes (vacío: sin tope)" unit="US$" inputMode="decimal" value={form.presupuestoMensual} onChange={(e) => setForm({ ...form, presupuestoMensual: e.target.value.replace(',', '.') })} />
            <Input label="Llamadas por minuto (vacío: sin límite)" inputMode="numeric" value={form.limiteRpm} onChange={(e) => setForm({ ...form, limiteRpm: e.target.value.replace(/\D/g, '') })} />
            {!form.id && <Input label="Vence en (días; vacío: no vence)" inputMode="numeric" value={form.diasVigencia} onChange={(e) => setForm({ ...form, diasVigencia: e.target.value.replace(/\D/g, '') })} />}
          </div>
          <Casilla checked={form.ocultarDatosPersonales} onChange={(v) => setForm({ ...form, ocultarDatosPersonales: v })}>
            Tapar correos, celulares, tarjetas y documentos antes de mandar un mensaje al proveedor del modelo
          </Casilla>
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}

      {nueva && (
        <Modal title="La llave está lista" subtitle="Se muestra una sola vez. Guardarla en el gestor de secretos del agente o de la aplicación." onClose={() => setNueva(null)} width={720}
          footer={<Button onClick={() => setNueva(null)}>Ya la guardé</Button>}>
          <Copiable texto={nueva.llave} etiqueta="Llave" />
          <ComoConectar llave={nueva.llave} modelo={nueva.modelo} />
        </Modal>
      )}
    </div>
  );
}
